import { create } from "zustand";
import { persist } from "zustand/middleware";
import { imageModels } from "@/config/models/image";

// Initialize default parameters for each model
const initializeDefaultParameters = () => {
  const defaultParams = {};

  Object.entries(imageModels).forEach(([modelId, model]) => {
    defaultParams[modelId] = {};
    Object.entries(model.parameters).forEach(([paramName, paramConfig]) => {
      defaultParams[modelId][paramName] = paramConfig.default;
    });
  });

  return defaultParams;
};

const useImageModelStore = create(
  persist(
    (set, get) => ({
      // Available image models
      models: imageModels,

      // Currently selected model (default to qwen-image)
      selectedModel: "qwen-image",

      // Currently selected editing model (default to gemini-2.5-flash or gpt-image-1)
      selectedEditModel: (() => {
        // Prefer gemini-2.5-flash (nano-banana) if available
        if (imageModels["gemini-2.5-flash"]?.capabilities?.editing) {
          return "gemini-2.5-flash";
        }
        // Fallback to gpt-image-1
        if (imageModels["gpt-image-1"]?.capabilities?.editing) {
          return "gpt-image-1";
        }
        // Last resort: find first editing-capable model
        const firstEditModel = Object.values(imageModels).find(
          (model) => model.capabilities?.editing
        );
        return firstEditModel?.id || "gpt-image-1";
      })(),

      // Model parameters (user's current settings per model)
      modelParameters: initializeDefaultParameters(),

      // Enabled generation models (all enabled by default)
      enabledGenerationModels: Object.keys(imageModels).reduce(
        (acc, modelId) => {
          acc[modelId] = true;
          return acc;
        },
        {}
      ),

      // Enabled edit models (all enabled by default)
      enabledEditModels: Object.keys(imageModels).reduce((acc, modelId) => {
        acc[modelId] = true;
        return acc;
      }, {}),

      // Actions
      setSelectedModel: (modelId) => {
        if (imageModels[modelId]) {
          set({ selectedModel: modelId });
        }
      },

      setSelectedEditModel: (modelId) => {
        if (
          imageModels[modelId] &&
          imageModels[modelId].capabilities?.editing
        ) {
          set({ selectedEditModel: modelId });
        }
      },

      getSelectedModel: () => {
        const state = get();
        return state.models[state.selectedModel];
      },

      getSelectedEditModel: () => {
        const state = get();
        return state.models[state.selectedEditModel];
      },

      // Model enable/disable actions
      toggleModelEnabled: (modelId, enabled, capability = "generation") => {
        // If trying to disable, check if this is the last enabled model
        if (!enabled) {
          const enabledModels = get().getEnabledModels(capability);
          if (enabledModels.length <= 1) {
            // Prevent disabling the last model
            return false;
          }
        }

        const stateKey =
          capability === "editing"
            ? "enabledEditModels"
            : "enabledGenerationModels";
        set((state) => ({
          [stateKey]: {
            ...state[stateKey],
            [modelId]: enabled,
          },
        }));
        return true;
      },

      isModelEnabled: (modelId, capability = "generation") => {
        const state = get();
        const stateKey =
          capability === "editing"
            ? "enabledEditModels"
            : "enabledGenerationModels";
        return state[stateKey][modelId] !== false; // Default to true
      },

      getEnabledModels: (capability = null) => {
        const state = get();
        if (!capability) {
          // Return all models that are enabled for either generation or editing
          return Object.values(state.models).filter(
            (model) =>
              state.enabledGenerationModels[model.id] !== false ||
              state.enabledEditModels[model.id] !== false
          );
        }

        const stateKey =
          capability === "editing"
            ? "enabledEditModels"
            : "enabledGenerationModels";
        return Object.values(state.models).filter(
          (model) =>
            model.capabilities[capability] &&
            state[stateKey][model.id] !== false
        );
      },

      updateModelParameter: (modelId, paramName, value) => {
        set((state) => ({
          modelParameters: {
            ...state.modelParameters,
            [modelId]: {
              ...state.modelParameters[modelId],
              [paramName]: value,
            },
          },
        }));
      },

      getModelParameters: (modelId) => {
        const state = get();
        return state.modelParameters[modelId] || {};
      },

      getCurrentModelParameters: () => {
        const state = get();
        return state.modelParameters[state.selectedModel] || {};
      },

      resetModelParameters: (modelId) => {
        const state = get();
        const model = state.models[modelId];
        if (!model) return;

        // Reset to default values from model config
        const defaults = {};
        Object.entries(model.parameters).forEach(([key, param]) => {
          defaults[key] = param.default;
        });

        set((state) => ({
          modelParameters: {
            ...state.modelParameters,
            [modelId]: defaults,
          },
        }));
      },

      resetCurrentModelParameters: () => {
        const state = get();
        get().resetModelParameters(state.selectedModel);
      },

      getModelsByProvider: (provider) => {
        const state = get();
        return Object.values(state.models).filter(
          (model) => model.provider === provider
        );
      },

      getModelCapabilities: (modelId) => {
        const state = get();
        return state.models[modelId]?.capabilities || {};
      },

      getCurrentModelCapabilities: () => {
        const state = get();
        return state.models[state.selectedModel]?.capabilities || {};
      },

      getAllModels: () => {
        const state = get();
        return Object.values(state.models);
      },

      // Get models grouped by provider for UI display
      getModelsByProviderGrouped: () => {
        const state = get();
        const grouped = {};

        Object.values(state.models).forEach((model) => {
          if (!grouped[model.provider]) {
            grouped[model.provider] = [];
          }
          grouped[model.provider].push(model);
        });

        return grouped;
      },

      // Check if a parameter is available for current model
      isParameterAvailable: (paramName) => {
        const state = get();
        const model = state.models[state.selectedModel];
        return model?.parameters?.[paramName] !== undefined;
      },

      // Get available editing models
      getEditableModels: () => {
        const state = get();
        return Object.values(state.models).filter(
          (model) => model.capabilities.editing === true
        );
      },
    }),
    {
      name: "image-model-storage",
      version: 2,
      // Only persist the selected model, parameters, and enabled models
      partialize: (state) => ({
        selectedModel: state.selectedModel,
        selectedEditModel: state.selectedEditModel,
        modelParameters: state.modelParameters,
        enabledGenerationModels: state.enabledGenerationModels,
        enabledEditModels: state.enabledEditModels,
      }),
    }
  )
);

export default useImageModelStore;
export { useImageModelStore };
